// Lazy Load Color Swatches for Product Cards
// Loads additional color swatches asynchronously after page load to improve performance
// First searches products already on the page, then uses search API as fallback

// Tags that split a product group into two separate colour pickers (e.g. "outlet").
// Sourced from window.hiddenSwatchTags, which theme.liquid renders from
// Theme settings > Product options > Hide swatches by tag.
//
// The rule is symmetric: a colour picker only ever shows siblings in the SAME
// bucket as the product being viewed. On an outlet product you see outlet
// colours only; on a current product you see current colours only. Every product
// stays fully shoppable either way — this only governs the swatch row.
function getHiddenSwatchTags() {
  const raw = window.hiddenSwatchTags;
  const list = typeof raw === 'string' ? raw.split(',') : Array.isArray(raw) ? raw : [];
  return list.map((tag) => String(tag).trim().toLowerCase()).filter(Boolean);
}

// Which bucket a product belongs to: true = carries a hidden tag (outlet side),
// false = current side. Accepts a Storefront API product node (tags array) or a
// plain array of tag strings.
function hasHiddenSwatchTag(productOrTags) {
  const hiddenTags = getHiddenSwatchTags();
  if (!hiddenTags.length) return false;
  const tags = Array.isArray(productOrTags)
    ? productOrTags
    : productOrTags && Array.isArray(productOrTags.tags)
    ? productOrTags.tags
    : [];
  return tags.some((tag) => hiddenTags.includes(String(tag).trim().toLowerCase()));
}

// Reads the bucket of the product a swatch container belongs to. Liquid stamps
// data-hidden-tagged="true" on the container when the current product carries a
// hidden tag; anything else means the current side.
function containerIsHiddenBucket(container) {
  return container?.dataset?.hiddenTagged === 'true';
}

// ---------------------------------------------------------------------------
// Swatch ordering
// ---------------------------------------------------------------------------
// Order of the colour row:
//   1. the colour being viewed  (rendered server-side, always stays first)
//   2. colours tagged "new", most in-stock sizes first
//   3. remaining colours, most in-stock sizes first
//   4. sold-out colours (zero sizes available) last, new ones first within that
// Ties break alphabetically on colour name so the order is identical on every
// PDP in the group and doesn't shuffle between page loads.
//
// "Inventory" here is the NUMBER OF SIZES IN STOCK, not units. Real unit counts
// need the `unauthenticated_read_product_inventory` scope on the Storefront
// token, which this store's token does not carry. If that scope is ever
// enabled, add `totalInventory` to nodeFields and return it from
// countInStockVariants() — nothing else in this file has to change.
function productIsNew(tags) {
  const list = Array.isArray(tags) ? tags : [];
  return list.some((tag) => String(tag).trim().toLowerCase() === 'new');
}

// Number of variants (sizes) available for sale. Returns null when the source
// didn't supply variant data, so we can fall back rather than sorting on a lie.
function countInStockVariants(product) {
  const edges = product?.variants?.edges;
  if (!Array.isArray(edges)) return null;
  return edges.reduce(
    (total, edge) => total + (edge?.node?.availableForSale ? 1 : 0),
    0
  );
}

// Resolve a sortable stock number for a swatch. When the real count is unknown
// we only know available vs not, so an available colour gets 1 (sorts above
// sold-out, below any colour with a known count of 2+).
function resolveStockCount(swatch) {
  if (typeof swatch.stockCount === 'number' && !Number.isNaN(swatch.stockCount)) {
    return swatch.stockCount;
  }
  return swatch.isUnavailable ? 0 : 1;
}

function sortSwatches(swatches) {
  return swatches
    .map((swatch, index) => ({ swatch, index }))
    .sort((a, b) => {
      const aStock = resolveStockCount(a.swatch);
      const bStock = resolveStockCount(b.swatch);
      const aSoldOut = aStock <= 0;
      const bSoldOut = bStock <= 0;

      // Sold-out colours always sink to the bottom, new tag or not.
      if (aSoldOut !== bSoldOut) return aSoldOut ? 1 : -1;

      // New drops lead their block.
      const aNew = a.swatch.isNew === true;
      const bNew = b.swatch.isNew === true;
      if (aNew !== bNew) return aNew ? -1 : 1;

      // Deepest size run first (sold-out colours are all 0, so this is a no-op
      // for them and they fall through to the alphabetical tie-break).
      if (bStock !== aStock) return bStock - aStock;

      const byName = (a.swatch.colorName || '').localeCompare(
        b.swatch.colorName || ''
      );
      if (byName !== 0) return byName;

      return a.index - b.index;
    })
    .map((entry) => entry.swatch);
}


// ---------------------------------------------------------------------------
// Style families: Rise / Inseam option on the PDP (added 2026-10-13)
// ---------------------------------------------------------------------------
// Separate products that differ only by rise ("Pump Mid Rise Legging" vs
// "Pump High Rise Legging") or by inseam ("Flared Pant" vs "Flared Pant Long")
// share a `family:<slug>` tag, maintained daily by scripts/sync_family_tags.py.
// On the PDP the loader fetches the whole family in ONE Storefront query and
// renders:
//   - a RISE and/or INSEAM toggle (only when the current bucket has 2+ values)
//   - the normal colour row = colours in the current rise + inseam
//   - a labelled row per other option ("Only available in High Rise") holding
//     colours the current option doesn't come in, greyed but clickable
// Products, variants and SKUs are untouched: every click is a normal product
// swap. Outlet/current buckets never mix (same rule as the colour row).
//
// parseStyleOptions MUST match parse() in scripts/sync_family_tags.py.
const FAMILY_RISE_ORDER = ['Low Rise', 'Mid Rise', 'High Rise', 'Ultra-High Rise', 'Super-High Rise'];
const FAMILY_LENGTH_ORDER = ['Short', 'Regular', 'Long'];
const FAMILY_RISE_RE = /\b(low|mid|high|ultra[- ]?high|super[- ]?high)[- ]rise\b/i;
const FAMILY_LENGTH_RE = /\b(pant|pants|legging|leggings|jogger|joggers|trouser|trousers|flare|flares)\s+(short|long)$/i;
const FAMILY_RISE_NAMES = {
  low: 'Low Rise',
  mid: 'Mid Rise',
  high: 'High Rise',
  ultrahigh: 'Ultra-High Rise',
  superhigh: 'Super-High Rise',
};

function parseStyleOptions(title) {
  let base = String(title || '').split(' - ')[0].trim();
  let rise = null;
  const riseMatch = base.match(FAMILY_RISE_RE);
  if (riseMatch) {
    rise = FAMILY_RISE_NAMES[riseMatch[1].toLowerCase().replace(/[- ]/g, '')] || null;
    base = base.replace(FAMILY_RISE_RE, ' ');
  }
  base = base.replace(/\s+/g, ' ').trim();
  let length = 'Regular';
  const lengthMatch = base.match(FAMILY_LENGTH_RE);
  if (lengthMatch) {
    length = lengthMatch[2].charAt(0).toUpperCase() + lengthMatch[2].slice(1).toLowerCase();
  }
  return { rise, length };
}

function familyColorKey(name) {
  return String(name || '').trim().toLowerCase();
}

// "High Rise", "Long", or "High Rise · Long" — what differs from the current option.
function familyOptionLabel(member, current) {
  const parts = [];
  if (member.rise && member.rise !== current.rise) parts.push(member.rise);
  if (member.length !== current.length) parts.push(`${member.length} Inseam`);
  return parts.join(' · ');
}

// Size carry-over: remember the selected size when the shopper switches colour,
// rise or inseam, and re-select it on the new product once the swap lands.
(function () {
  let pendingSize = null;

  function mainProductInfo() {
    return document.querySelector('product-info[data-main-product]');
  }

  function sizeFieldset(root) {
    if (!root) return null;
    const fieldsets = root.querySelectorAll('product-variant-selects fieldset');
    for (const fieldset of fieldsets) {
      const legend = (fieldset.querySelector('legend')?.textContent || '').toUpperCase();
      if (legend.includes('SIZE')) return fieldset;
    }
    return null;
  }

  window.alphaleteRememberSize = function () {
    const fieldset = sizeFieldset(mainProductInfo());
    const checked = fieldset?.querySelector('input[type="radio"]:checked');
    pendingSize = checked ? checked.value : null;
  };

  window.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest?.(
        '.product-page__color-swatch, [data-family-url]'
      );
      if (!trigger || !trigger.closest('product-info[data-main-product]')) return;
      window.alphaleteRememberSize();
    },
    true
  );

  document.addEventListener('product-info:loaded', () => {
    if (!pendingSize) return;
    const wanted = pendingSize.trim().toLowerCase();
    pendingSize = null;
    // Runs after product-page-swatch-switcher clears the size (enforceNoDefaultVariant).
    setTimeout(() => {
      const fieldset = sizeFieldset(mainProductInfo());
      if (!fieldset) return;
      const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
        (radio) => String(radio.value).trim().toLowerCase() === wanted
      );
      if (input && !input.checked) input.click();
    }, 350);
  });
})();

// Rise / Inseam toggle clicks → same in-place product swap as a colour swatch.
document.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-family-url]');
  if (!button) return;
  event.preventDefault();
  const url = button.dataset.familyUrl;
  if (!url || button.getAttribute('aria-pressed') === 'true') return;
  if (typeof window.alphaleteSwapMainProduct === 'function') {
    window.alphaleteSwapMainProduct(url);
  } else {
    window.location.href = url;
  }
});

class ProductCardSwatchLoader {
  constructor() {
    this.loadedSwatches = new Set(); // Track which swatch containers have been loaded
    this.maxSwatches = 40;
    this.intersectionObserver = null;
    this.mutationObserver = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.startLoading());
    } else {
      this.startLoading();
    }
  }

  startLoading() {
    // Load product page swatches immediately (they're always visible)
    const productPageSwatches = document.querySelectorAll(
      '.product-page__color-swatches[data-lazy-load-swatches="true"]'
    );
    if (productPageSwatches.length > 0) {
      productPageSwatches.forEach((container) => {
        const containerId = container.dataset.currentProductId;
        if (containerId) {
          this.loadSwatchesForContainer(container);
        }
      });
    }

    // Use requestIdleCallback for better performance, fallback to setTimeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.loadSwatches(), { timeout: 2000 });
    } else {
      setTimeout(() => this.loadSwatches(), 1000);
    }

    // Setup Intersection Observer for cards that come into view
    this.setupIntersectionObserver();

    // Setup Mutation Observer to watch for new product cards (infinite scroll)
    this.setupMutationObserver();

    // Listen for product swap events to reload swatches
    this.setupProductSwapListener();
  }

  setupProductSwapListener() {
    // Listen for product-info loaded event (fires after product swap)
    document.addEventListener(
      'product-info:loaded',
      (e) => {
        // Wait a bit for DOM to settle
        setTimeout(() => {
          const productPageSwatches = document.querySelectorAll(
            '.product-page__color-swatches[data-lazy-load-swatches="true"]'
          );
          productPageSwatches.forEach((container) => {
            const containerId = container.dataset.currentProductId;
            if (containerId) {
              // Reset the loaded state for this container to allow reloading
              const containerKey = `${containerId}-${
                container.dataset.baseTitle || ''
              }`;
              this.loadedSwatches.delete(containerKey);
              // Reload swatches for the new product
              this.loadSwatchesForContainer(container);
            }
          });
        }, 100);
      },
      false
    );
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      // Fallback: load all swatches after delay
      setTimeout(() => this.loadSwatches(), 2000);
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const container = entry.target;
            const containerId = container.dataset.currentProductId;
            if (containerId && !this.loadedSwatches.has(containerId)) {
              this.loadSwatchesForContainer(container);
            }
          }
        });
      },
      {
        rootMargin: '50px', // Start loading slightly before card is visible
        threshold: 0.1,
      }
    );

    // Observe all existing swatch containers
    this.observeContainers();
  }

  setupMutationObserver() {
    if (!('MutationObserver' in window)) {
      // Fallback: periodically check for new containers
      setInterval(() => this.checkForNewContainers(), 2000);
      return;
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      let hasNewContainers = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          // Check if the added node is a product card, product page swatch container, or contains them
          if (node.nodeType === 1) {
            // Check if node itself is a product card or swatch container
            if (
              node.matches &&
              (node.matches('product-card') ||
                node.matches('[data-lazy-load-swatches="true"]') ||
                node.matches('.product-page__color-swatches'))
            ) {
              hasNewContainers = true;
            }
            // Check if node contains product cards or swatch containers
            const productCards = node.querySelectorAll
              ? node.querySelectorAll(
                  'product-card, [data-lazy-load-swatches="true"]'
                )
              : [];
            if (productCards.length > 0) {
              hasNewContainers = true;
            }
          }
        });
      });

      if (hasNewContainers) {
        // Use requestIdleCallback or small delay to batch DOM queries
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => {
            this.observeContainers();
            this.loadSwatches();
          });
        } else {
          setTimeout(() => {
            this.observeContainers();
            this.loadSwatches();
          }, 100);
        }
      }
    });

    // Observe the document body for changes
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  observeContainers() {
    if (!this.intersectionObserver) {
      return;
    }

    // Find all swatch containers that aren't being observed yet
    const containers = document.querySelectorAll(
      '[data-lazy-load-swatches="true"]'
    );

    containers.forEach((container) => {
      const containerId = container.dataset.currentProductId;
      const containerKey = `${containerId}-${
        container.dataset.baseTitle || ''
      }`;
      // Only observe if not already loaded and has valid ID
      if (containerId && !this.loadedSwatches.has(containerKey)) {
        this.intersectionObserver.observe(container);
      }
    });
  }

  checkForNewContainers() {
    const containers = document.querySelectorAll(
      '[data-lazy-load-swatches="true"]'
    );
    let hasNew = false;

    containers.forEach((container) => {
      const containerId = container.dataset.currentProductId;
      const containerKey = `${containerId}-${
        container.dataset.baseTitle || ''
      }`;
      if (containerId && !this.loadedSwatches.has(containerKey)) {
        hasNew = true;
        this.observeContainers();
      }
    });

    if (hasNew) {
      this.loadSwatches();
    }
  }

  async loadSwatches() {
    const containers = document.querySelectorAll(
      '[data-lazy-load-swatches="true"]'
    );

    // Load swatches in batches to avoid overwhelming the browser
    const batchSize = 10;
    for (let i = 0; i < containers.length; i += batchSize) {
      const batch = Array.from(containers).slice(i, i + batchSize);
      await Promise.all(
        batch.map((container) => this.loadSwatchesForContainer(container))
      );

      // Small delay between batches
      if (i + batchSize < containers.length) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    }
  }

  async loadSwatchesForContainer(container) {
    const containerId = container.dataset.currentProductId;
    if (!containerId) {
      return;
    }

    const baseTitle = container.dataset.baseTitle;
    if (!baseTitle) {
      return;
    }

    // Check if this container was already loaded (prevent duplicate loading)
    // But allow reloading if the container was replaced (product swap)
    const containerKey = `${containerId}-${baseTitle}`;
    // Family PDPs can legitimately have a 1-swatch main row; don't rebuild them
    // on every DOM mutation.
    if (
      this.loadedSwatches.has(containerKey) &&
      container.dataset.familyLoaded === 'true'
    ) {
      return;
    }
    if (this.loadedSwatches.has(containerKey)) {
      // Check if container still has the same structure (not replaced)
      const existingSwatches = container.querySelectorAll(
        '.product-card__color-swatch, .product-page__color-swatch'
      );
      // If there's only 1 swatch (the current product), it might need reloading
      if (existingSwatches.length > 1) {
        return; // Already loaded and has multiple swatches
      }
    }

    this.loadedSwatches.add(containerKey);

    // Clear ALL existing swatches except the first one (current product)
    this.clearExistingSwatches(container);

    // Get the current product's swatch to ensure we don't include it
    const currentSwatch = container.querySelector(
      '.product-card__color-swatch, .product-page__color-swatch'
    );
    const currentProductId = currentSwatch?.dataset.productId || containerId;

    // Style family (Rise / Inseam option): one query for every rise + inseam.
    // Falls back to the normal sid colour row if the family can't be loaded.
    if (
      container.dataset.family &&
      container.classList.contains('product-page__color-swatches')
    ) {
      const handled = await this.loadFamilySwatches(container, currentSwatch);
      if (handled) return;
    }

    // Use a Set to track all product IDs we've found (ensures uniqueness)
    const foundProductIds = new Set([currentProductId]);
    const swatches = [];

    // First, try to find swatches from other containers on the page
    // But ONLY get the FIRST swatch from each container (represents that product)
    const allContainers = document.querySelectorAll(
      '[data-lazy-load-swatches="true"]'
    );

    for (const otherContainer of allContainers) {
      if (swatches.length >= this.maxSwatches) {
        break;
      }

      // Skip current container
      if (otherContainer === container) {
        continue;
      }

      // Must have matching base title
      const otherBaseTitle = otherContainer.dataset.baseTitle;
      if (!otherBaseTitle || otherBaseTitle !== baseTitle) {
        continue;
      }

      // Get ONLY the first swatch from this container (the product's own swatch)
      const firstSwatch =
        otherContainer.querySelector(
          '.product-card__color-swatch:first-child, .product-page__color-swatch:first-child'
        ) ||
        otherContainer.querySelector(
          '.product-card__color-swatch, .product-page__color-swatch'
        );

      if (!firstSwatch) {
        continue;
      }

      const productId = firstSwatch.dataset.productId;
      if (!productId || foundProductIds.has(productId)) {
        continue;
      }

      foundProductIds.add(productId);

      swatches.push({
        color: firstSwatch.dataset.color,
        colorName: firstSwatch.dataset.colorName || '',
        productId: firstSwatch.dataset.productId,
        productHandle: firstSwatch.dataset.productHandle,
        productUrl: firstSwatch.dataset.productUrl,
        backgroundColor: firstSwatch.style.backgroundColor,
        isUnavailable:
          firstSwatch.classList.contains(
            'product-card__color-swatch--unavailable'
          ) ||
          firstSwatch.classList.contains(
            'product-page__color-swatch--unavailable'
          ),
        isProductPage: firstSwatch.classList.contains(
          'product-page__color-swatch'
        ),
        refined: firstSwatch.dataset.refined === 'true',
        isNew: firstSwatch.dataset.isNew === 'true',
        stockCount:
          firstSwatch.dataset.stockCount !== undefined
            ? Number(firstSwatch.dataset.stockCount)
            : null,
      });
    }

    // First, check if we have matching products data from Liquid (product cards only - current collection)
    const matchingProductsData = container.dataset.matchingProducts;
    if (matchingProductsData) {
      try {
        const matchingProducts = JSON.parse(matchingProductsData);
        matchingProducts.forEach((product) => {
          if (
            swatches.length < this.maxSwatches &&
            product.id &&
            product.id !== currentProductId &&
            !foundProductIds.has(product.id)
          ) {
            foundProductIds.add(product.id);
            swatches.push({
              color: product.color || '',
              colorName: product.colorName || product.color_name || '',
              productId: product.id,
              productHandle: product.handle,
              productUrl: product.url,
              backgroundColor: product.swatch || '',
              isUnavailable: !product.available || product.unavailable,
              isProductPage: container.classList.contains(
                'product-page__color-swatches'
              ),
              refined: product.refined === true,
              isNew: product.new === true,
              stockCount:
                typeof product.instock === 'number' ? product.instock : null,
            });
          }
        });
      } catch (e) {
        console.debug('Error parsing matching products data:', e);
      }
    }

    // If we need more swatches, use search API (more efficient - single request)
    if (swatches.length < this.maxSwatches) {
      const apiSwatches = await this.findSwatchesViaSearchAPI(
        baseTitle,
        currentProductId,
        foundProductIds,
        container.dataset.sid,
        containerIsHiddenBucket(container)
      );

      // Add API swatches, ensuring no duplicates
      for (const apiSwatch of apiSwatches) {
        if (swatches.length >= this.maxSwatches) {
          break;
        }
        if (
          apiSwatch.productId &&
          !foundProductIds.has(apiSwatch.productId) &&
          apiSwatch.productId !== currentProductId
        ) {
          foundProductIds.add(apiSwatch.productId);
          swatches.push(apiSwatch);
        }
      }
    }

    // Before rendering, check what swatches already exist in the container
    // and filter out any duplicates
    const existingSwatches = container.querySelectorAll(
      '.product-card__color-swatch, .product-page__color-swatch'
    );
    const existingProductIds = new Set();
    existingSwatches.forEach((swatch) => {
      const productId = swatch.dataset.productId;
      if (productId) {
        existingProductIds.add(productId);
      }
    });

    // Filter out swatches that already exist in the container
    const uniqueSwatches = swatches.filter(
      (swatch) => !existingProductIds.has(swatch.productId)
    );

    // New-tagged colours first, then deepest size run, sold-out last.
    // The current colour is server-rendered and is never part of this array,
    // so it keeps position 1 regardless.
    const orderedSwatches = sortSwatches(uniqueSwatches);

    // Render swatches (only if we have any new ones)
    if (orderedSwatches.length > 0) {
      this.renderSwatches(container, orderedSwatches);
    }
  }

  clearExistingSwatches(container) {
    const isProductPage = container.classList.contains(
      'product-page__color-swatches'
    );
    const wrapper = isProductPage
      ? container
      : container.querySelector('.swiper-wrapper');

    if (!wrapper) {
      return;
    }

    // Keep the first swatch (current product), remove the rest
    const allSwatches = wrapper.querySelectorAll(
      '.product-card__color-swatch, .product-page__color-swatch'
    );

    if (isProductPage) {
      // For product pages, remove all except the first
      allSwatches.forEach((swatch, index) => {
        if (index > 0) {
          swatch.remove();
        }
      });
    } else {
      // For product cards, remove slides except the first
      const slides = wrapper.querySelectorAll('.swiper-slide');
      slides.forEach((slide, index) => {
        if (index > 0) {
          slide.remove();
        }
      });
    }
  }

  findSwatchesOnPage(container, baseTitle, currentProductId) {
    const swatches = [];
    const foundProductIds = new Set([currentProductId]); // Always exclude current product
    const isProductPage = container.classList.contains(
      'product-page__color-swatches'
    );

    // Get all swatches from the current container to exclude them
    const currentSwatches = container.querySelectorAll(
      '.product-card__color-swatch, .product-page__color-swatch'
    );
    const currentSwatchProductIds = new Set();
    currentSwatches.forEach((swatch) => {
      const productId = swatch.dataset.productId;
      if (productId) {
        currentSwatchProductIds.add(productId);
      }
    });

    // Find all swatch containers on the page (both product cards and product pages)
    const allSwatchContainers = document.querySelectorAll(
      '[data-lazy-load-swatches="true"]'
    );

    allSwatchContainers.forEach((swatchContainer) => {
      if (swatches.length >= this.maxSwatches) {
        return;
      }

      // Skip the current container
      if (swatchContainer === container) {
        return;
      }

      const containerBaseTitle = swatchContainer.dataset.baseTitle;
      // Must match base title exactly
      if (!containerBaseTitle || containerBaseTitle !== baseTitle) {
        return;
      }

      // Get the FIRST swatch from this container (represents that product)
      // This ensures we only get one swatch per product
      let existingSwatch = swatchContainer.querySelector(
        '.product-card__color-swatch:first-of-type, .product-page__color-swatch:first-of-type'
      );

      // Fallback if :first-of-type doesn't work
      if (!existingSwatch) {
        const cardSwatch = swatchContainer.querySelector(
          '.product-card__color-swatch'
        );
        const pageSwatch = swatchContainer.querySelector(
          '.product-page__color-swatch'
        );
        existingSwatch = cardSwatch || pageSwatch;
      }

      if (!existingSwatch) {
        return;
      }

      const productId = existingSwatch.dataset.productId;
      // Skip if:
      // - No product ID
      // - Already found (duplicate)
      // - Is the current product
      // - Already exists in current container
      if (
        !productId ||
        foundProductIds.has(productId) ||
        productId === currentProductId ||
        currentSwatchProductIds.has(productId)
      ) {
        return;
      }

      foundProductIds.add(productId);

      const swatchData = {
        color: existingSwatch.dataset.color,
        productId: existingSwatch.dataset.productId,
        productHandle: existingSwatch.dataset.productHandle,
        productUrl: existingSwatch.dataset.productUrl,
        backgroundColor: existingSwatch.style.backgroundColor,
        isUnavailable:
          existingSwatch.classList.contains(
            'product-card__color-swatch--unavailable'
          ) ||
          existingSwatch.classList.contains(
            'product-page__color-swatch--unavailable'
          ),
        isProductPage: existingSwatch.classList.contains(
          'product-page__color-swatch'
        ),
      };

      swatches.push(swatchData);
    });

    return swatches;
  }

  async findSwatchesViaSearchAPI(
    baseTitle,
    currentProductId,
    existingProductIds,
    sid,
    currentIsHiddenBucket
  ) {
    const swatches = [];
    const foundProductIds = new Set([
      currentProductId,
      ...(existingProductIds || []),
    ]);

    // Cache the Storefront result for the whole colour GROUP, 5 min TTL, so we
    // don't re-fire the costly query on every container and every refresh.
    //
    // Two properties matter here; getting either wrong silently drops colours.
    //
    //   1. The key is the GROUP (sid + bucket), not the current product. Every
    //      product in a group has the identical sibling list, and a single PDP
    //      renders several containers carrying the SAME currentProductId (the
    //      colour picker, plus recommendation cards for that same product), so a
    //      per-product key had them sharing one entry.
    //
    //   2. The cached payload is the FULL group, with nothing subtracted. It used
    //      to store the list already filtered by `existingProductIds` — whatever
    //      sibling swatches happened to be in the DOM at the moment that
    //      container ran. Whichever container wrote first therefore poisoned
    //      every later reader with a short list for the next 5 minutes. Because
    //      it depended on when Shopify's recommendation cards landed in the DOM,
    //      it hit some PDPs and not others, and the colours it ate were the ones
    //      surfaced as recommendations — typically the newest drops.
    //
    // Per-caller exclusions are applied on the way OUT, in excludeFound().
    const cacheKey =
      'alSwatchCache6:' +
      (sid || baseTitle || '') +
      ':' +
      (currentIsHiddenBucket ? 'outlet' : 'current');
    const excludeFound = (list) =>
      (list || []).filter((sw) => sw && !foundProductIds.has(sw.productId));
    const readCache = () => {
      try {
        const c = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
        if (c && Array.isArray(c.s) && Date.now() - c.t < 300000) {
          return c.s;
        }
      } catch (e) {}
      return null;
    };
    const cachedGroup = readCache();
    if (cachedGroup) {
      return excludeFound(cachedGroup);
    }

    try {
      // Get storefront access token from global window object
      const accessToken = window.storefrontAccessToken;
      if (!accessToken) {
        console.debug(
          'Storefront Access Token is missing. Please add it in theme settings.'
        );
        return swatches;
      }

      // Get shop URL
      const shopUrl = window.shopUrl || window.Shopify?.shop || '';
      if (!shopUrl) {
        console.debug('Shop URL is missing.');
        return swatches;
      }

      // Extract domain from shop URL (handle both full URLs and domains)
      const shopDomain = shopUrl.includes('://')
        ? new URL(shopUrl).hostname
        : shopUrl.replace(/^https?:\/\//, '').split('/')[0];

      // Build the query: prefer exact group membership via the shared sid tag
      // (reliable even when two products share an identical title, e.g. two Blacks);
      // fall back to base-title search when no sid is available.
      const useTag = !!sid;
      const nodeFields = `
                id
                title
                handle
                availableForSale
                onlineStoreUrl
                featuredImage {
                  id
                  src
                }
                color_swatch: metafield(namespace: "custom", key: "color_swatch") {
                  value
                  type
                }
                refined_fit: metafield(namespace: "custom", key: "refined_fit") {
                  value
                }
                tags
                variants(first: 50) {
                  edges {
                    node {
                      availableForSale
                    }
                  }
                }`;
      const graphqlQuery = useTag
        ? `query swatchesByTag($query: String!, $first: Int) {
        products(query: $query, first: $first) {
          edges { node { ${nodeFields} } }
        }
      }`
        : `query searchProducts($query: String!, $first: Int) {
        search(query: $query, first: $first, types: PRODUCT) {
          edges { node { ... on Product { ${nodeFields} } } }
        }
      }`;
      // Scope the query to the current product's bucket so the other bucket
      // doesn't eat into the `first: 100` budget. On an outlet product we ask for
      // siblings carrying any hidden tag; on a current product we exclude them
      // all. The search path can't reliably express this, so both paths are also
      // filtered client-side below.
      const hiddenTags = getHiddenSwatchTags();
      let bucketQuery = '';
      if (hiddenTags.length) {
        const escaped = hiddenTags.map((tag) => tag.replace(/'/g, "\\'"));
        bucketQuery = currentIsHiddenBucket
          ? ` AND (${escaped.map((tag) => `tag:'${tag}'`).join(' OR ')})`
          : escaped.map((tag) => ` AND -tag:'${tag}'`).join('');
      }
      const queryString = useTag ? `tag:'${sid}'${bucketQuery}` : baseTitle;

      // Use relative path to avoid CORS issues
      const graphqlUrl = `/api/2025-04/graphql.json`;

      const response = await fetch(graphqlUrl, {
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': accessToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: graphqlQuery,
          variables: {
            query: queryString,
            first: useTag ? 100 : 200,
          },
        }),
      });

      if (!response.ok) {
        console.debug('GraphQL request failed:', response.status);
        return excludeFound(readCache() || swatches);
      }

      const result = await response.json();

      if (result.errors) {
        console.debug('GraphQL Errors:', result.errors);
        return excludeFound(readCache() || swatches);
      }

      // Tag query already scopes to the exact group; title search needs base-title filtering.
      const edges = useTag
        ? result.data?.products?.edges
        : result.data?.search?.edges;
      const products = (edges?.map((edge) => edge.node) || []).filter(
        (product) => {
          if (!product.title) return false;
          // Ensure product is published to Online Store
          if (!product.onlineStoreUrl || product.onlineStoreUrl === '') {
            return false;
          }
          // Keep only siblings in the same bucket as the product being viewed:
          // outlet pages show outlet colours, current pages show current colours.
          if (hasHiddenSwatchTag(product) !== !!currentIsHiddenBucket) {
            return false;
          }
          if (useTag) return true;
          // Extract base title before " - Color"
          const productBase = product.title.split(' - ')[0]?.trim();
          const currentBase = baseTitle?.trim();
          return productBase === currentBase;
        }
      );

      // If the tag query returned nothing (e.g. unusual tag formatting), fall back
      // to the legacy base-title search so we never regress to zero siblings.
      if (useTag && products.length === 0) {
        return this.findSwatchesViaSearchAPI(
          baseTitle,
          currentProductId,
          existingProductIds,
          null,
          currentIsHiddenBucket
        );
      }

      // Tracks duplicates inside this response only (see note in the loop).
      const groupProductIds = new Set();

      for (const product of products) {
        // maxSwatches now caps the cached GROUP, not the rendered remainder.
        if (swatches.length >= this.maxSwatches) {
          break;
        }

        // Extract product ID (remove 'gid://shopify/Product/' prefix)
        const productId = product.id.split('/').pop();

        // Only de-duplicate within this response. The current product and any
        // siblings already rendered elsewhere stay IN the group we cache — they
        // are removed per caller by excludeFound() below, so the cached payload
        // is the same no matter which product page built it.
        if (!productId || groupProductIds.has(productId)) {
          continue;
        }

        // Extract color name from title (everything after ' - ')
        const colorName = product.title.split(' - ').pop()?.trim() || '';
        const colorSwatch = product.color_swatch;

        // Skip if no color swatch metafield
        if (!colorSwatch || !colorSwatch.value) {
          continue;
        }

        groupProductIds.add(productId);

        // Build product URL
        const productUrl = `/products/${product.handle}`;

        const swatchData = {
          color: colorName ? colorName.toLowerCase().replace(/\s+/g, '-') : '',
          colorName: colorName,
          productId: productId,
          productHandle: product.handle,
          productUrl: productUrl,
          backgroundColor: colorSwatch.value,
          isUnavailable: !product.availableForSale,
          isProductPage: false,
          refined: product.refined_fit?.value === 'true',
          isNew: productIsNew(product.tags),
          stockCount: countInStockVariants(product),
        };

        swatches.push(swatchData);
      }

      try {
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({ t: Date.now(), s: swatches })
        );
      } catch (e) {}
    } catch (error) {
      console.debug('Error loading swatches via GraphQL API:', error);
      const cachedOnError = readCache();
      if (cachedOnError) return excludeFound(cachedOnError);
    }

    return excludeFound(swatches);
  }


  // ---- Style families (Rise / Inseam) -------------------------------------
  // Whole family for this bucket, cached 5 min. Unlike the sid path there is no
  // maxSwatches cap here: Pump families run ~70 products across both rises.
  async fetchFamilyGroup(family, currentIsHiddenBucket) {
    const cacheKey =
      'alFamilyCache1:' + family + ':' + (currentIsHiddenBucket ? 'outlet' : 'current');
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (cached && Array.isArray(cached.s) && Date.now() - cached.t < 300000) {
        return cached.s;
      }
    } catch (e) {}

    const accessToken = window.storefrontAccessToken;
    if (!accessToken) return null;

    const hiddenTags = getHiddenSwatchTags();
    let bucketQuery = '';
    if (hiddenTags.length) {
      const escaped = hiddenTags.map((tag) => tag.replace(/'/g, "\\'"));
      bucketQuery = currentIsHiddenBucket
        ? ` AND (${escaped.map((tag) => `tag:'${tag}'`).join(' OR ')})`
        : escaped.map((tag) => ` AND -tag:'${tag}'`).join('');
    }

    const query = `query familyByTag($query: String!, $first: Int) {
      products(query: $query, first: $first) {
        edges { node {
          id title handle availableForSale onlineStoreUrl tags
          color_swatch: metafield(namespace: "custom", key: "color_swatch") { value }
          refined_fit: metafield(namespace: "custom", key: "refined_fit") { value }
          variants(first: 50) { edges { node { availableForSale } } }
        } }
      }
    }`;

    try {
      const response = await fetch('/api/2025-04/graphql.json', {
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': accessToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          variables: { query: `tag:'${family.replace(/'/g, "\\'")}'${bucketQuery}`, first: 150 },
        }),
      });
      if (!response.ok) return null;
      const result = await response.json();
      if (result.errors) {
        console.debug('Family query errors:', result.errors);
        return null;
      }

      const seen = new Set();
      const members = [];
      for (const edge of result.data?.products?.edges || []) {
        const product = edge.node;
        if (!product?.title || !product.onlineStoreUrl) continue;
        if (hasHiddenSwatchTag(product) !== !!currentIsHiddenBucket) continue;
        if (!product.color_swatch?.value) continue;
        const productId = product.id.split('/').pop();
        if (seen.has(productId)) continue;
        seen.add(productId);
        const colorName = product.title.split(' - ').pop()?.trim() || '';
        const options = parseStyleOptions(product.title);
        members.push({
          color: colorName.toLowerCase().replace(/\s+/g, '-'),
          colorName,
          productId,
          productHandle: product.handle,
          productUrl: `/products/${product.handle}`,
          backgroundColor: product.color_swatch.value,
          isUnavailable: !product.availableForSale,
          isProductPage: true,
          refined: product.refined_fit?.value === 'true',
          isNew: productIsNew(product.tags),
          stockCount: countInStockVariants(product),
          rise: options.rise,
          length: options.length,
        });
      }
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ t: Date.now(), s: members }));
      } catch (e) {}
      return members;
    } catch (error) {
      console.debug('Error loading family:', error);
      return null;
    }
  }

  // Returns true when the family UI was rendered; false → caller falls back to
  // the normal sid colour row.
  async loadFamilySwatches(container, currentSwatch) {
    const family = container.dataset.family;
    const group = await this.fetchFamilyGroup(family, containerIsHiddenBucket(container));
    if (!group || group.length === 0) return false;

    const current = parseStyleOptions(container.dataset.currentTitle);
    const currentId = currentSwatch?.dataset.productId || container.dataset.currentProductId;
    const currentColorKey = familyColorKey(currentSwatch?.dataset.colorName);
    const currentRefined = currentSwatch?.dataset.refined === 'true';
    const sameRise = (m) => (m.rise || null) === (current.rise || null);
    const sameLength = (m) => m.length === current.length;

    // 1. Normal colour row: same rise + inseam as the product being viewed.
    const sameOption = group.filter((m) => sameRise(m) && sameLength(m));
    const mainRow = sortSwatches(
      sameOption.filter((m) => m.productId !== currentId)
    ).slice(0, this.maxSwatches);
    if (mainRow.length) this.renderSwatches(container, mainRow);

    // 2. Colours this option doesn't come in → one labelled, greyed row per option.
    const ownColors = new Set(sameOption.map((m) => familyColorKey(m.colorName)));
    ownColors.add(currentColorKey);
    const bestByColor = new Map();
    const preference = (m) =>
      (sameLength(m) ? 0 : sameRise(m) ? 1 : 2) * 10 + (m.isUnavailable ? 1 : 0);
    for (const m of group) {
      if (sameRise(m) && sameLength(m)) continue;
      const key = familyColorKey(m.colorName);
      if (ownColors.has(key)) continue;
      const incumbent = bestByColor.get(key);
      if (!incumbent || preference(m) < preference(incumbent)) bestByColor.set(key, m);
    }
    const altGroups = new Map();
    for (const m of bestByColor.values()) {
      const label = familyOptionLabel(m, current);
      if (!altGroups.has(label)) altGroups.set(label, []);
      altGroups.get(label).push(m);
    }

    const block = container.closest('.product-page__color-swatches-container');
    const altHost = block?.querySelector('[data-family-alt]');
    if (altHost) {
      altHost.innerHTML = '';
      const labels = Array.from(altGroups.keys()).sort();
      for (const label of labels) {
        const section = document.createElement('div');
        section.className = 'pdp-family-alt__group';
        const heading = document.createElement('p');
        heading.className = 'pdp-family-alt__label';
        heading.textContent = `Only available in ${label}`;
        const row = document.createElement('div');
        row.className = 'pdp-family-alt__swatches';
        section.appendChild(heading);
        section.appendChild(row);
        altHost.appendChild(section);
        const swatches = sortSwatches(altGroups.get(label)).map((m) => ({
          ...m,
          colorName: `${m.colorName} — ${label}`,
          extraClass: 'product-page__color-swatch--other-option',
        }));
        this.renderSwatches(container, swatches, row);
      }
      altHost.hidden = labels.length === 0;
    }

    // 3. Rise / Inseam toggles.
    const optionsHost = block?.querySelector('[data-family-options]');
    if (optionsHost) {
      optionsHost.innerHTML = '';
      const dims = [
        { key: 'rise', legend: 'Rise', order: FAMILY_RISE_ORDER, same: sameLength },
        { key: 'length', legend: 'Inseam', order: FAMILY_LENGTH_ORDER, same: sameRise },
      ];
      let rendered = 0;
      for (const dim of dims) {
        const values = Array.from(new Set(group.map((m) => m[dim.key]).filter(Boolean)));
        if (values.length < 2 || !current[dim.key]) continue;
        values.sort((a, b) => dim.order.indexOf(a) - dim.order.indexOf(b));

        const fieldset = document.createElement('div');
        fieldset.className = 'pdp-family-option';
        fieldset.setAttribute('role', 'group');
        fieldset.setAttribute('aria-label', dim.legend);
        const legend = document.createElement('p');
        legend.className = 'pdp-family-option__legend';
        legend.textContent = dim.legend;
        const buttons = document.createElement('div');
        buttons.className = 'pdp-family-option__buttons';
        fieldset.appendChild(legend);
        fieldset.appendChild(buttons);

        for (const value of values) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'pdp-family-option__btn';
          button.textContent = value;
          if (value === current[dim.key]) {
            button.classList.add('is-active');
            button.setAttribute('aria-pressed', 'true');
            buttons.appendChild(button);
            continue;
          }
          button.setAttribute('aria-pressed', 'false');
          // Hold the other dimension where possible (High Rise keeps Regular inseam).
          let candidates = group.filter((m) => m[dim.key] === value && dim.same(m));
          if (!candidates.length) candidates = group.filter((m) => m[dim.key] === value);
          const sameColor = candidates
            .filter((m) => familyColorKey(m.colorName) === currentColorKey)
            .sort(
              (a, b) =>
                (a.refined === currentRefined ? 0 : 1) - (b.refined === currentRefined ? 0 : 1) ||
                (a.isUnavailable ? 1 : 0) - (b.isUnavailable ? 1 : 0)
            );
          let target;
          if (sameColor.length) {
            target = sameColor[0];
          } else {
            // Not made in this colour → greyed; lands on that option's top colour.
            target = sortSwatches(candidates)[0];
            button.classList.add('is-unavailable');
            const colorLabel = currentSwatch?.dataset.colorName || 'This color';
            button.title = `${colorLabel} isn't available in ${value} — tap to see ${value} colors`;
            button.setAttribute('aria-label', `${value} (not available in ${colorLabel})`);
          }
          if (!target) continue;
          button.dataset.familyUrl = target.productUrl;
          buttons.appendChild(button);
        }
        optionsHost.appendChild(fieldset);
        rendered++;
      }
      optionsHost.hidden = rendered === 0;
    }

    container.dataset.familyLoaded = 'true';

    // If a size is already picked (e.g. carried over from the previous colour),
    // apply the size check to the rows that just rendered.
    if (typeof window.alphaleteRefreshSwatchAvailability === 'function') {
      setTimeout(() => window.alphaleteRefreshSwatchAvailability(), 0);
    }
    return true;
  }

  // targetWrapper (optional): render PDP swatches into another element — used
  // for the family "Only available in …" rows.
  renderSwatches(container, swatches, targetWrapper) {
    const isProductPage =
      !!targetWrapper ||
      container.classList.contains('product-page__color-swatches');
    const wrapper = targetWrapper
      ? targetWrapper
      : isProductPage
      ? container
      : container.querySelector('.swiper-wrapper');

    if (!wrapper) {
      return;
    }

    swatches.forEach((swatch, index) => {
      const swatchClass = isProductPage
        ? 'product-page__color-swatch product-page__color-swatch--fade-in'
        : 'product-card__color-swatch product-card__color-swatch--fade-in';

      const unavailableClass = isProductPage
        ? 'product-page__color-swatch--unavailable'
        : 'product-card__color-swatch--unavailable';

      const labelClass = isProductPage
        ? 'product-page__color-swatch-label'
        : 'product-card__color-swatch-label';

      const swatchDiv = document.createElement('div');
      swatchDiv.className = `${swatchClass}${
        swatch.isUnavailable ? ` ${unavailableClass}` : ''
      }${swatch.extraClass ? ` ${swatch.extraClass}` : ''}`;
      swatchDiv.dataset.color = swatch.color;
      if (swatch.colorName) {
        swatchDiv.dataset.colorName = swatch.colorName;
      }
      swatchDiv.dataset.productId = swatch.productId;
      swatchDiv.dataset.productHandle = swatch.productHandle;
      swatchDiv.dataset.productUrl = swatch.productUrl;
      if (swatch.refined) {
        swatchDiv.dataset.refined = 'true';
      }
      if (swatch.isNew) {
        swatchDiv.dataset.isNew = 'true';
      }
      if (typeof swatch.stockCount === 'number') {
        swatchDiv.dataset.stockCount = String(swatch.stockCount);
      }

      // Set size based on type
      if (isProductPage) {
        swatchDiv.style.cssText = `background-color: ${swatch.backgroundColor}; width: 2.5rem; height: 2.5rem; cursor: pointer;`;
      } else {
        swatchDiv.style.cssText = `background-color: ${swatch.backgroundColor}; min-width: 2rem; min-height: 2rem; cursor: pointer;`;
      }

      const label = document.createElement('span');
      label.className = labelClass;
      swatchDiv.appendChild(label);

      // Product cards use swiper slides, product pages don't
      if (isProductPage) {
        wrapper.appendChild(swatchDiv);
      } else {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.appendChild(swatchDiv);
        wrapper.appendChild(slide);
      }

      // Stagger the fade-in animation for each swatch
      setTimeout(() => {
        const fadeInActiveClass = isProductPage
          ? 'product-page__color-swatch--fade-in-active'
          : 'product-card__color-swatch--fade-in-active';
        swatchDiv.classList.add(fadeInActiveClass);
      }, index * 50); // 50ms delay between each swatch
    });

    // Reinitialize Swiper only for product cards (product pages don't use Swiper)
    if (!isProductPage && typeof Swiper !== 'undefined') {
      if (container.swiper) {
        container.swiper.update();
      } else {
        // Initialize Swiper if not already initialized
        const swiperOptions = JSON.parse(
          container.dataset.swiperOptions || '{}'
        );
        new Swiper(container, {
          freeMode: true,
          slidesPerView: 'auto',
          spaceBetween: 8,
          enabled: true,
          ...swiperOptions,
        });
      }
    }
  }
}

// Initialize when DOM is ready
try {
  new ProductCardSwatchLoader();
} catch (error) {
  console.error('Error initializing ProductCardSwatchLoader:', error);
}
